import { useCallback } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { each, includes, pickBy } from 'lodash-es'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DataClass, DataItem, navigateTo } from 'scrivito'

import { getSchemaAttributes } from '../read/schema'
import { buildValidationSchema } from '../validation/buildValidationSchema'
import { errorToast } from '@/Components/Toast'
import { DefaultValuesPayload } from '@/Data/defaultValues'
import { useDefaultsPrefill } from '@/hooks/useDefaultsPrefill'
import { getSchemaName } from '@/utils/dataClassMap'

export const useDataClassFormHandler = (
  dataClass: DataClass,
  options?: {
    prefill?: { enabled?: boolean; payload?: DefaultValuesPayload }
    onCreated?: (dataItem: DataItem) => void
    navigateOnCreated?: boolean
    attributeOptions?: {
      [attribute: string]: { default?: string }
    }
  },
) => {
  const { t } = useTranslation()
  const schemaName = getSchemaName(dataClass.name()!)
  const { schemaGroups, groupsOrder, schemaAttributes } =
    getSchemaAttributes(schemaName)

  // Build validation schema for visible mandatory attributes
  const validationSchemaObject = buildValidationSchema(
    schemaGroups,
    groupsOrder ?? [],
    schemaAttributes ?? {},
    t,
  )

  const formResolver = yupResolver(validationSchemaObject)

  const methods = useForm({ resolver: formResolver })
  const { handleSubmit, reset } = methods

  useDefaultsPrefill({
    enabled: options?.prefill?.enabled ?? false,
    payload: options?.prefill?.payload ?? { entityName: schemaName },
    form: methods,
  })

  const onCancelClick = () => {
    reset()
  }

  const onSubmit = (data: Record<string, unknown>) => {
    data = applyDefaults(data, options?.attributeOptions)
    const definedData = pickBy(data, (e) => e !== undefined)

    dataClass
      .create(definedData)
      .then((dataItem) => {
        options?.onCreated?.(dataItem)
        if (options?.navigateOnCreated !== false) {
          navigateTo(dataItem)
        }
      })
      .catch((e) => errorToast(e))
  }

  const onFormSubmit = handleSubmit(onSubmit)

  const handleSave = useCallback(() => {
    onFormSubmit()
  }, [onFormSubmit])

  return { methods, onCancelClick, onSubmit, onFormSubmit, handleSave }
}

/**
 * Adds default values for attributes that where not in the form
 * @param data form data
 * @param attributeOptions defaults
 * @returns form data enriched by default values
 */
const applyDefaults = (
  data: Record<string, unknown>,
  attributeOptions: {
    [attribute: string]: { default?: string }
  } = {},
) => {
  const dataKeys = Object.keys(data)

  each(Object.keys(attributeOptions), (attribute) => {
    if (attributeOptions?.[attribute]?.default) {
      if (!includes(dataKeys, attribute)) {
        data[attribute] = attributeOptions?.[attribute]?.default
      }
    }
  })

  return data
}
